import { FADE_IN_BOUNCE_PROPS } from '#app/components/Motion/constants.js';
import { type BaseProps as AnimatedBaseProps } from '#app/components/Primitives/Animated.js';
import { logger } from '#app/services/logger.js';
import { ModalContext } from '../context.js';
import { type ModalBaseProps } from '../types.js';
import {
  Backdrop,
  BackdropBackground,
  Dialog,
  DialogContainer,
  HeightTransitionContainer,
  OutsideOfModalContainer,
} from './Modal.styles.js';
import { ModalCloseButton } from './ModalCloseButton.js';
import { ModalErrorBoundary } from './ModalErrorBoundary.js';
import { useMount } from '@contra/react-hooks/useMount';
import { useDialog } from '@react-aria/dialog';
import { FocusScope, type FocusScopeProps } from '@react-aria/focus';
import { OverlayContainer, useModal, useOverlay } from '@react-aria/overlays';
import { useMemo, useRef, useState } from 'react';

const MaybeFocusScope = ({
  children,
  preventAutoFocus,
  preventFocusContain,
  ...props
}: FocusScopeProps & {
  readonly preventAutoFocus?: boolean;
  readonly preventFocusContain?: boolean;
}) => {
  if (preventFocusContain) {
    return <>{children}</>;
  }

  return (
    <FocusScope
      autoFocus={!preventAutoFocus}
      contain
      restoreFocus
      {...props}
    >
      {children}
    </FocusScope>
  );
};

const log = logger.child({
  namespace: 'modal',
});

type ModalDialogProps = ModalBaseProps & { readonly ariaLabel: string };

/**
 * This component is needing to be separate from `<Modal>` so we can
 * guarantee that when it's rendered the element potentially rendering
 * `titleProps` is rendered in the DOM when `useDialog` is called.
 *
 * If we don't do this we won't get our generated `id` and `aria-labelledby` props.
 *
 * `<Modal />` conditionally renders this component based on `isOpen`.
 */
export const ModalDialog = ({
  ariaLabel,
  blurBackdrop = false,
  children,
  customBackdropOpacity,
  customBelowModalContent,
  customZIndex,
  isDismissible = false,
  isKeyboardDismissDisabled = false,
  isOpen,
  onClose,
  overflowX,
  overrideBackgroundColor,
  overrideBackgroundImageUrl,
  overrideBoxShadow,
  preventAutoFocus = false,
  preventFocusContain = false,
  role,
  size = 'md',
  ...props
}: ModalDialogProps) => {
  // Handle interactions outside the dialog
  // and using Escape key to dismiss.
  const ref = useRef<HTMLDivElement>(null);
  const { overlayProps } = useOverlay(
    {
      isDismissable: isDismissible,
      isKeyboardDismissDisabled,
      isOpen,
      onClose,
      shouldCloseOnBlur: false,
    },
    ref,
  );

  // Prevent scrolling while modal is open
  // and hide content outside modal from screen readers.
  const { modalProps } = useModal();

  // Props for the dialog and it's title.
  // We need to guarantee this is called when receiving DOM elements are actually rendered.
  // If not, then you won't get a generated ID. (ARIA)
  const { dialogProps, titleProps } = useDialog(
    { ...props, 'aria-label': ariaLabel, role },
    ref,
  );

  // If you see this warning, you have forgotten to either:
  //
  // 1. Use a `<Modal.Title>`, or use `titleProps` from `useModalContext()` (if visible title is rendered).
  // -- or --
  // 2. Provide either a `aria-label` or `aria-labelledby` prop. `aria-labelledby` should point to an unique `id`.

  // Temporarily disabling the below warning due to noise. Need to investigate why titleProps is not being generated even though we give it a title and aria-label.
  // Task to investigate - https://linear.app/contra/issue/GRO-478

  // Prefer a visible title over aria props.
  if (!titleProps.id) {
    log.debug(
      'Modal is missing usage of `titleProps`, or missing `aria-label` or `aria-labelledby` props.',
    );
  }

  // Asserting type here because Motion defines it's own handler types
  // that don't line up with the default HTML types.
  const modalDialogProps = {
    ...overlayProps,
    ...dialogProps,
    ...modalProps,
  } as AnimatedBaseProps<'div'>;

  const context = useMemo(() => {
    return { isDismissible, onClose, size, titleProps };
  }, [isDismissible, onClose, size, titleProps]);

  const heightTransitionContainerRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<'auto' | number>('auto');

  useMount(() => {
    if (heightTransitionContainerRef.current && ResizeObserver) {
      const resizeObserver = new ResizeObserver((entries) => {
        // We only have one entry, so we can use entries[0].
        const observedHeight = entries[0].contentRect.height;
        setHeight(observedHeight);
      });

      resizeObserver.observe(heightTransitionContainerRef.current);

      return () => {
        // Cleanup the observer when the component is unmounted
        resizeObserver.disconnect();
      };
    }

    return () => {};
  });

  return (
    <OverlayContainer id="modal-v2-overlay-container">
      <Backdrop
        blurBackdrop={blurBackdrop}
        customZIndex={customZIndex}
        key="backdrop"
        onClick={isDismissible ? (event) => event.stopPropagation() : undefined}
        style={{
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      >
        <DialogContainer $size={size}>
          <MaybeFocusScope
            preventAutoFocus={preventAutoFocus}
            preventFocusContain={preventFocusContain}
          >
            {size === 'fullWidth' ? (
              <ModalCloseButton
                closeIconColor="white"
                onClose={onClose}
              />
            ) : null}

            <ModalContext.Provider value={context}>
              <Dialog
                $overrideBoxShadow={overrideBoxShadow}
                key="modal"
                overflowX={overflowX}
                overrideBackgroundColor={overrideBackgroundColor}
                overrideBackgroundImageUrl={overrideBackgroundImageUrl}
                {...modalDialogProps}
                $size={size}
                animate={{ height, ...FADE_IN_BOUNCE_PROPS.animate }}
                customZIndex={customZIndex}
                initial={{ height, ...FADE_IN_BOUNCE_PROPS }}
                ref={ref}
                style={{
                  height: size === 'fullscreen' ? 'auto' : `${height}px`,
                }}
                transition={{
                  height: { duration: 0.3, ease: [0.23, 1, 0.32, 1] },
                  ...FADE_IN_BOUNCE_PROPS.transition,
                }}
              >
                <HeightTransitionContainer ref={heightTransitionContainerRef}>
                  <ModalErrorBoundary modalName={props.modalName}>
                    {children}
                  </ModalErrorBoundary>
                </HeightTransitionContainer>
                {customBelowModalContent ? (
                  <OutsideOfModalContainer>
                    {customBelowModalContent}
                  </OutsideOfModalContainer>
                ) : null}
              </Dialog>
            </ModalContext.Provider>
          </MaybeFocusScope>
        </DialogContainer>
        <BackdropBackground $customOpacity={customBackdropOpacity} />
      </Backdrop>
    </OverlayContainer>
  );
};
